import React, {
  useContext,
  useEffect,
  useMemo,
  Dispatch,
  SetStateAction,
  useCallback,
} from "react";
import { AxiosError } from "axios";
import { useInfiniteQuery, useQuery } from "react-query";
import { useLocation, useParams, useRouteMatch } from "react-router-dom";

import UserContext from "contexts/userContext";
import { ApiError } from "fe-shared/src/types/common";
import { GridInvoice, GridInvoiceList, Invoice } from "fe-shared-transactions/src/types";

import useCancelableRequest from "fe-shared/src/hooks/useCancelableRequest";

import { hasPermission } from "fe-shared/src/helpers/permissions";
import { PermissionLevel } from "fe-shared/src/types/domain";

import { PERMISSIONS, INVOICES_QUERY_KEY, INVOICES_NAVIGATION_QUERY_KEY, NAVIGATION_PAGE_SIZE, INVOICES_LISTINFO_QUERY_KEY } from "consts";

import InvoicesDetailsView from "../InvoicesDetailsView";

import * as api from "../modules/api";
import useInvoicesRequestParams from "../../../hooks/useInvoicesRequestParams";
import { NavigationInvoices } from "../modules/api";
import useNavigationType from "fe-shared/src/hooks/useNavigationType";

interface Props {
  setLastOpenedInvoiceNumber: Dispatch<SetStateAction<string | null>>;
}

const InvoicesDetailsViewContainer = ({
  setLastOpenedInvoiceNumber,
}: Props) => {
  const { invoiceNumber } = useParams<{ invoiceNumber: string }>();
  const { search } = useLocation();
  const { path } = useRouteMatch();
  const user = useContext(UserContext);
  const { requestParams } = useInvoicesRequestParams();
  const canViewInvoiceDetails = hasPermission(user?.permissions, {
    name: PERMISSIONS.invoiceDetails,
    level: PermissionLevel.Read,
  });
  const returnUrl = useMemo(() => path.replace("/:invoiceNumber", ""), [path]);
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const isWithNavigation = searchParams.get("navigation") === "true";
  const { isDirectNavigation } = useNavigationType();

  const getInvoiceCancelToken = useCancelableRequest();
  const getInvoiceExistValidationCancelToken = useCancelableRequest();

  useEffect(() => {
    if (invoiceNumber) {
      setLastOpenedInvoiceNumber(invoiceNumber);
    }
  }, [setLastOpenedInvoiceNumber, invoiceNumber]);

  const invoiceListInfoQueryKey = [INVOICES_LISTINFO_QUERY_KEY, invoiceNumber];
  const fetchInvoiceListInfo = useCallback(async () => {
    const cancelToken = getInvoiceExistValidationCancelToken(INVOICES_LISTINFO_QUERY_KEY);
    return api.getInvoiceListInfo(INVOICES_LISTINFO_QUERY_KEY, invoiceNumber, requestParams, cancelToken);
  }, [invoiceNumber, requestParams]);

  const invoiceListInfo = useQuery<api.InvoiceListInfo, AxiosError<ApiError>>(
    invoiceListInfoQueryKey,
    fetchInvoiceListInfo,
    {
      enabled: isWithNavigation && isDirectNavigation,
      refetchOnWindowFocus: false,
      keepPreviousData: false
    }
  );
  const invoiceQueryKey = [INVOICES_QUERY_KEY, invoiceNumber];

  const fetchInvoice = useCallback(async () => {
    const cancelToken = getInvoiceCancelToken(INVOICES_QUERY_KEY);
    return api.getInvoice(INVOICES_QUERY_KEY, invoiceNumber, cancelToken);
  }, [invoiceNumber]);
  const invoice = useQuery<Invoice, AxiosError<ApiError>>(
    invoiceQueryKey,
    fetchInvoice,
    {
      keepPreviousData: true,
      enabled: canViewInvoiceDetails,
    }
  );

  const navigationInvoicesQueryKey = [
      INVOICES_NAVIGATION_QUERY_KEY,
      requestParams,
  ];
  const navigationInvoices = useInfiniteQuery<
    NavigationInvoices,
    AxiosError<ApiError>
  >(navigationInvoicesQueryKey, api.getNavigationInvoices, {
    enabled: isWithNavigation,
    refetchOnWindowFocus: false,
    keepPreviousData: false,
    getFetchMore: (lastGroup, allGroups) =>
      allGroups.length * NAVIGATION_PAGE_SIZE < lastGroup.total
        ? allGroups.length + 1
        : undefined,
  });

  return (
    <InvoicesDetailsView
      returnUrl={returnUrl}
      invoice={invoice}
      isWithNavigation={isWithNavigation}
      invoiceQueryKey={invoiceQueryKey}
      navigationInvoices={navigationInvoices}
      invoiceListInfo={invoiceListInfo}
      isDirectNavigation={isDirectNavigation}
      isFetching={invoiceNumber !== invoice.data?.invoiceNumber}
    />
  );
};

export default InvoicesDetailsViewContainer;
